import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        blackOutline: "border border-input bg-background text-black hover:bg-gray-100",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // Minimal transparent with a soft border — readable on white cards
        glass:
          "bg-transparent border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 backdrop-blur-sm",
        // Glass variant for destructive / danger actions
        "glass-danger":
          "bg-transparent border border-red-200 text-red-500 hover:bg-red-50/70 hover:border-red-300 backdrop-blur-sm",
        // Glass variant for success / confirm actions
        "glass-success":
          "bg-transparent border border-emerald-200 text-emerald-600 hover:bg-emerald-50/70 hover:border-emerald-300 backdrop-blur-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-7 px-2.5 text-xs rounded-md",
        md: "h-8 px-3 text-xs rounded-lg",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
